import React, { useCallback, useEffect, useState } from "react";
import {
  getAuth,
  signInWithPopup,
  OAuthProvider,
  signInWithCredential,
  signInWithRedirect,
  getRedirectResult,
  UserCredential,
} from "firebase/auth";
import "./AppleSignIn.css";
import { FormRow } from "../pages/Common/LandingPage/SectionContent/AboutContent";
import Button from "../components/Button/Button";
import { useTheme } from "@emotion/react";
import { SiApple } from "react-icons/si";
import { useUserControls } from "../context/UserControlsProvider";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import { isIOSWeb, isNativePlatform } from "../utils/platform";

type SnackbarFn = (options: {
  message: string;
  severity?: "success" | "info" | "warning" | "error";
  duration?: number;
}) => void;

interface SignInWithAppleOptions {
  onSuccessfulLogin?: () => void;
  showSnackbar?: SnackbarFn;
}

const APPLE_PROVIDER_ID = "apple.com";

function createAppleProvider() {
  const provider = new OAuthProvider(APPLE_PROVIDER_ID);
  provider.addScope("email");
  provider.addScope("name");
  const locale =
    typeof navigator !== "undefined" && navigator.language
      ? navigator.language.replace("-", "_")
      : "en_US";
  provider.setCustomParameters({ locale });
  return provider;
}

async function handleWebCredential(
  result: UserCredential,
  { onSuccessfulLogin, showSnackbar }: SignInWithAppleOptions,
) {
  if (!result?.user) {
    throw new Error("Apple sign-in did not return a user.");
  }
  const token = await result.user.getIdToken();
  console.log("[AppleSignIn] Firebase ID Token:", token);
  showSnackbar?.({
    message: "Signed in with Apple.",
    severity: "success",
  });
  onSuccessfulLogin?.();
  return result;
}

async function handleAppleRedirectResult(
  options: SignInWithAppleOptions,
) {
  if (isNativePlatform()) return false;
  try {
    const auth = getAuth();
    const redirectResult = await getRedirectResult(auth);
    if (!redirectResult || !redirectResult.user) {
      return false;
    }
    await handleWebCredential(redirectResult, options);
    return true;
  } catch (error: any) {
    if (error?.code) {
      const message = deriveAppleErrorMessage(
        (error.code ?? "").toString(),
        error?.message,
      );
      options.showSnackbar?.({
        message,
        severity: "error",
        duration: 6000,
      });
    }
    console.error("[AppleSignIn] Failed to resume redirect result.", error);
    return false;
  }
}

async function beginRedirectFlow(
  auth: ReturnType<typeof getAuth>,
  provider: OAuthProvider,
  showSnackbar?: SnackbarFn,
) {
  showSnackbar?.({
    message: "Continuing Apple sign-in...",
    severity: "info",
    duration: 4000,
  });
  await signInWithRedirect(auth, provider);
}

function shouldForceRedirectFlow() {
  if (isNativePlatform()) return false;
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isSafari =
    /Safari/.test(ua) && !/(Chrome|CriOS|FxiOS|Edg|OPR)/.test(ua);
  return isIOSWeb() || isSafari;
}

function shouldFallbackToRedirect(code: string) {
  if (!code) return false;
  return (
    code.includes("operation-not-supported") ||
    code.includes("popup-blocked")
  );
}

export const signInWithApple = async ({
  onSuccessfulLogin,
  showSnackbar,
}: SignInWithAppleOptions = {}) => {
  const auth = getAuth();
  const provider = createAppleProvider();

  try {
    if (isNativePlatform()) {
      const nativeResult = await FirebaseAuthentication.signInWithApple({
        skipNativeAuth: true,
        scopes: ["email", "name"],
      });
      const idToken = nativeResult.credential?.idToken;
      const rawNonce = nativeResult.credential?.nonce;
      if (!idToken) {
        throw new Error("Missing Apple ID token from native credential.");
      }
      const credential = provider.credential({
        idToken,
        rawNonce,
      });
      const userCredential = await signInWithCredential(auth, credential);
      console.log("[AppleSignIn] Native user signed in:", userCredential.user.uid);
      showSnackbar?.({
        message: "Signed in with Apple.",
        severity: "success",
      });
      onSuccessfulLogin?.();
      return userCredential;
    }

    if (shouldForceRedirectFlow()) {
      await beginRedirectFlow(auth, provider, showSnackbar);
      return null;
    }

    const result = await signInWithPopup(auth, provider);
    return await handleWebCredential(result, { onSuccessfulLogin, showSnackbar });
  } catch (error: any) {
    console.error("Error signing in with Apple: ", error);
    const code = (error?.code ?? "").toString();
    if (!isNativePlatform() && shouldFallbackToRedirect(code)) {
      console.warn(
        "[AppleSignIn] Falling back to redirect-based auth because popup failed.",
      );
      await beginRedirectFlow(auth, provider, showSnackbar);
      return null;
    }
    const message = deriveAppleErrorMessage(code, error?.message);
    showSnackbar?.({
      message,
      severity: "error",
      duration: 6000,
    });
    throw error;
  }
};

interface AppleSignInButtonProps {
  onSuccessfulLogin?: () => void;
}

const AppleSignInButton: React.FC<AppleSignInButtonProps> = ({
  onSuccessfulLogin,
}) => {
  const theme: any = useTheme();
  const { showSnackbar } = useUserControls();
  const [loading, setLoading] = useState(false);
  const [initializingRedirect, setInitializingRedirect] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const checkRedirect = async () => {
      try {
        await handleAppleRedirectResult({ onSuccessfulLogin, showSnackbar });
      } finally {
        if (!cancelled) {
          setInitializingRedirect(false);
        }
      }
    };
    checkRedirect();
    return () => {
      cancelled = true;
    };
  }, [onSuccessfulLogin, showSnackbar]);

  const handleClick = useCallback(async () => {
    if (loading || initializingRedirect) return;
    setLoading(true);
    try {
      await signInWithApple({ onSuccessfulLogin, showSnackbar });
    } catch {
      // Error notification handled inside signInWithApple
    } finally {
      setLoading(false);
    }
  }, [loading, initializingRedirect, onSuccessfulLogin, showSnackbar]);

  return (
    <FormRow labelWidth="100%">
      <Button
        size="sm"
        onClick={handleClick}
        disabled={loading}
        aria-busy={loading || initializingRedirect}
        aria-label="Sign in with Apple"
        style={{
          width: "100%",
          marginTop: ".25rem",
          padding: ".75rem",
          backgroundColor: `color-mix(in srgb, ${theme.background}, ${theme.foreground} 5%)`,
          color: theme.foreground,
        }}
      >
        <SiApple
          size="1.5rem"
          color={theme.foreground}
          style={{ position: "relative", bottom: "3px", marginRight: ".5rem" }}
        />
        {loading || initializingRedirect ? "Signing in..." : "Sign in with Apple"}
      </Button>
    </FormRow>
  );
};

export default AppleSignInButton;

const deriveAppleErrorMessage = (code: string, fallback?: string) => {
  if (code.includes("operation-not-supported")) {
    return "Apple sign-in is unavailable in this environment.";
  }
  if (code.includes("popup-blocked")) {
    return "Your browser blocked the Apple sign-in window. Allow popups and retry.";
  }
  if (code.includes("popup-closed-by-user")) {
    return "Apple sign-in was closed before completing.";
  }
  if (code.includes("argument-error")) {
    return "Native Apple sign-in is not fully configured. Confirm the Services ID and reversed client ID.";
  }
  return fallback || "Unable to sign in with Apple. Please try again.";
};
